import { capitalize, isEqual, uniq } from 'es-toolkit';

import { ComponentDesign } from '@paragon/contract-library';
import { designService, LockTarget } from '@paragon/data-connections';
import { componentDesignsBatchLocked } from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { getAppMonitor } from '../../monitor';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import {
  setLockedHangers,
  setLockedMembers,
  setLockedPlatePairs,
  setLockOptions,
} from '../slices/componentDesignsSlice';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export type LockOptions = Pick<
  ComponentDesign,
  'analogLocked' | 'hangersLocked' | 'platingLocked' | 'loadingLocked' | 'membersLocked'
>;

export const defaultLockOptions: LockOptions = {
  analogLocked: false,
  hangersLocked: false,
  platingLocked: false,
  loadingLocked: false,
  membersLocked: false,
};

export const areLockOptionsDefault = (lockOptions: LockOptions) => isEqual(lockOptions, defaultLockOptions);

export const getLockOptionsFromComponentDesign = (componentDesign: ComponentDesign): LockOptions => ({
  analogLocked: componentDesign.analogLocked,
  hangersLocked: componentDesign.hangersLocked,
  platingLocked: componentDesign.platingLocked,
  loadingLocked: componentDesign.loadingLocked,
  membersLocked: componentDesign.membersLocked,
});

export const setLockOptionForComponentDesign =
  (componentDesignGuid: string, option: keyof LockOptions, value: boolean): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!componentDesign) throw new Error('Component design not found.');
    const lockOptions: LockOptions = {
      hangersLocked: componentDesign.hangersLocked,
      platingLocked: componentDesign.platingLocked,
      loadingLocked: componentDesign.loadingLocked,
      membersLocked: componentDesign.membersLocked,
      // A special case: locking loading should also lock the analog (https://trello.com/c/8aJGloOU)
      analogLocked: option === 'loadingLocked' && value ? true : componentDesign.analogLocked,
      [option]: value,
    };
    dispatch(setLockOptions({ componentDesignGuid, lockOptions }));
  };

export const lockIndividualHangers =
  (lock: boolean, ...hangerGuidsToLock: string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) throw new Error('Active component design not found');

    if (componentDesign.hangersLocked) {
      dispatch(enqueueError('You cannot lock/unlock an individual hanger when all hangers are locked'));
      return;
    }

    const componentDesignGuid = componentDesign.guid;

    const lockedHangers = lock
      ? uniq([...componentDesign.lockedHangers, ...hangerGuidsToLock])
      : componentDesign.lockedHangers.filter((guid) => !hangerGuidsToLock.includes(guid));

    dispatch(setLockedHangers({ componentDesignGuid, lockedHangers }));
  };

export const lockIndividualMember =
  (lock: boolean, ...memberGuidsToLock: string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) throw new Error('Active component design not found');

    if (componentDesign.membersLocked) {
      dispatch(enqueueError('You cannot lock/unlock an individual member when all members are locked'));
      return;
    }

    const componentDesignGuid = componentDesign.guid;

    const lockedMembers = lock
      ? uniq([...componentDesign.lockedMembers, ...memberGuidsToLock])
      : componentDesign.lockedMembers.filter((guid) => !memberGuidsToLock.includes(guid));

    dispatch(setLockedMembers({ componentDesignGuid, lockedMembers }));
  };

export const lockIndividualPlatePairs =
  (lock: boolean, ...platePairGuidsToLock: string[]): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    const componentDesign = getActiveComponentDesign(state);
    if (!componentDesign) throw new Error('Active component design not found.');

    const componentDesignGuid = componentDesign.guid;

    let updatedLockedPlatePairs: string[];

    // If plating is locked for the whole component, unlock the whole component and instead lock every individual plate
    //   except for the ones being unlocked.
    if (lock === false && componentDesign.platingLocked) {
      dispatch(
        setLockOptions({
          componentDesignGuid,
          lockOptions: {
            platingLocked: false,
            hangersLocked: componentDesign.hangersLocked,
            loadingLocked: componentDesign.loadingLocked,
            membersLocked: componentDesign.membersLocked,
            analogLocked: componentDesign.analogLocked,
          },
        }),
      );

      updatedLockedPlatePairs = componentDesign.component.platePairs
        .filter((platePair) => !platePairGuidsToLock.includes(platePair.guid))
        .map((platePair) => platePair.guid);
    } else {
      updatedLockedPlatePairs = lock
        ? uniq([...componentDesign.lockedPlatePairs, ...platePairGuidsToLock])
        : componentDesign.lockedPlatePairs.filter((guid) => !platePairGuidsToLock.includes(guid));
    }

    dispatch(
      setLockedPlatePairs({
        componentDesignGuid: componentDesign.guid,
        lockedPlatePairs: updatedLockedPlatePairs,
      }),
    );
  };

export const batchLock =
  (componentDesignGuids: string[], lockTarget: LockTarget, value: boolean): AsyncThunkActionVoid =>
  async (dispatch) => {
    const spinnerId = dispatch(addSpinnerMessage(`${value ? 'Locking' : 'Unlocking'} component designs...`));

    try {
      const updatedSummariesAndTimestamps = await designService.componentDesigns.componentDesignsBatchLockAction({
        componentDesignGuids,
        lockTargets: [lockTarget],
        value,
      });

      // Clear component designs/summaries that were affected, and replace them with the updated summaries and
      // timestamps from the route.
      dispatch(componentDesignsBatchLocked({ updatedSummariesAndTimestamps, lockTargets: [lockTarget] }));

      // TODO: apply to layout models as well?
    } catch {
      dispatch(enqueueError(`Error ${value ? 'locking' : 'unlocking'} component designs`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const batchAffectAllLocks =
  (action: 'lock' | 'unlock') =>
  (componentDesignGuids: string[], lockTargets: readonly LockTarget[]): AsyncThunkActionVoid =>
  async (dispatch) => {
    // Recorded here rather than in the menu item so that a cancelled confirmation isn't counted as an unlock.
    getAppMonitor().recordInteraction(`Everything ${action}ed for batch of components`);

    const spinnerId = dispatch(addSpinnerMessage(`${capitalize(action)}ing component designs...`));

    try {
      const updatedSummariesAndTimestamps = await designService.componentDesigns.componentDesignsBatchLockAction({
        componentDesignGuids,
        lockTargets,
        value: action === 'lock',
      });

      dispatch(componentDesignsBatchLocked({ updatedSummariesAndTimestamps, lockTargets }));
    } catch {
      dispatch(enqueueError(`Error ${action}ing component designs`));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const batchLockEverything = batchAffectAllLocks('lock');
export const batchUnlockEverything = batchAffectAllLocks('unlock');
